import { request, resource, stream } from './api'

// Every backend resource the app touches, in one place.
export const auth = {
  login: (username, password) => request('auth', '/token/', { method: 'POST', body: { username, password }, auth: false }),
  verifyToken: (token) => request('auth', '/token/verify/', { method: 'POST', body: { token }, auth: false }),
  logout: () => request('auth', '/logout/', { method: 'POST', body: {} }),
  forgot: (email, resend) => request('auth', '/forgot/', { method: 'POST', body: resend ? { email, resend: true } : { email }, auth: false }),
  verify: (body) => request('auth', '/verify/', { method: 'POST', body, auth: false }),
  reset: (body) => request('auth', '/reset/', { method: 'PUT', body, auth: false }),
  status: () => request('auth', '/status/'),
  impersonate: (user_id) => request('auth', '/impersonation/', { method: 'POST', body: { user_id } }),
  changeEmail: (body) => request('auth', '/user/email/', { method: 'PUT', body }),
  changePassword: (body) => request('auth', '/user/password/', { method: 'PUT', body }),
}

export const users = resource('auth', '/users/')
export const profiles = resource('auth', '/profiles/')
export const bankAccounts = resource('auth', '/bankaccounts/')
export const authGroups = resource('auth', '/groups/')
export const permissions = resource('auth', '/permissions/')
export const exceptions = resource('auth', '/exceptions/')
export const logs = resource('auth', '/logs/')

export const websites = {
  ...resource('mgmt', '/websites/'),
  status: () => request('mgmt', '/websites/status/'),
  stages: (body) => request('mgmt', '/websites/stages/', { method: 'POST', body }),
}
export const groupsTypes = resource('mgmt', '/groups_types/')
export const tags = resource('mgmt', '/tags/')
export const placementGroups = resource('mgmt', '/placements_groups/')
export const placementData = resource('mgmt', '/placements_groups_data/data/')
export const serveMetrics = resource('mgmt', '/placements_groups_data/metrics/')
export const suppliers = resource('mgmt', '/suppliers/')
export const adsLines = {
  ...resource('mgmt', '/ads/ads/'),
  txt: () => request('mgmt', '/ads/txt/'),
}
export const fillRate = resource('mgmt', '/fill_rate/')
export const shares = {
  adcrew: resource('mgmt', '/shares/adcrew/'),
  pmp: resource('mgmt', '/shares/pmp/'),
  network: resource('mgmt', '/shares/network/'),
  supplier: resource('mgmt', '/shares/supplier/'),
}
export const dataStatistics = {
  ...resource('mgmt', '/data_statistics/'),
  status: () => request('mgmt', '/data_statistics/status/'),
  requestData: (id, date) => request('mgmt', '/data_statistics/request/data/', { method: 'POST', body: { id, date } }),
  // {start_date, end_date, group_by, website?, pgid?, pgdid_list?, supplier?, type?, complete?}
  //   -> {group_by, results: [{key, revenue, impressions, clicks, rows}], totals}
  summary: (params) => request('mgmt', '/data_statistics/summary/', { params }),
}
export const groupTotals = {
  ...resource('mgmt', '/group_totals/'),
  status: () => request('mgmt', '/group_totals/status/'),
  requestGroup: (id, date) => request('mgmt', '/group_totals/request/group/', { method: 'POST', body: { id, date } }),
  // {start_date, end_date, pgid} -> {results: [{date, revenue, impressions, clicks?}], totals}
  daily: (params) => request('mgmt', '/group_totals/daily/', { params }),
}
export const websiteTotals = {
  ...resource('mgmt', '/user_website_totals/'),
  status: () => request('mgmt', '/user_website_totals/status/'),
  requestWebsite: (id, date) => request('mgmt', '/user_website_totals/request/website/', { method: 'POST', body: { id, date } }),
  // {start_date, end_date, website?} -> {results: [{date, revenue, impressions, clicks?}], totals}
  daily: (params) => request('mgmt', '/user_website_totals/daily/', { params }),
}
export const wallets = resource('mgmt', '/wallets/wallets/')
export const invoices = resource('mgmt', '/wallets/invoices/')
export const accountWallets = resource('mgmt', '/wallets/accounts/')
export const whitelist = {
  ...resource('mgmt', '/whitelist/'),
  check: (domain) => request('mgmt', '/whitelist/check/', { method: 'POST', body: { domain }, auth: false }),
}
export const system = { metrics: () => request('mgmt', '/system/metrics/') }
export const apiKeys = resource('mgmt', '/auth/api-keys/')
export const assigned = resource('mgmt', '/auth/assigned/')
export const externalApis = {
  types: () => request('mgmt', '/apis/types/'),
  of: (type) => resource('mgmt', `/apis/${type}/`),
}
export const purgeCache = (url) => request('mgmt', '/purge_cache/purge/', { method: 'POST', body: { url } })
export const scheduler = {
  jobs: resource('mgmt', '/scheduler/jobs/'),
  toggle: (id) => request('mgmt', `/scheduler/jobs/${id}/toggle/`, { method: 'POST' }),
  run: (id) => request('mgmt', `/scheduler/jobs/${id}/run/`, { method: 'POST' }),
  runNow: (params) => request('mgmt', '/scheduler/run-now/', { method: 'POST', params }),
  refresh: () => request('mgmt', '/scheduler/refresh/', { method: 'POST' }),
  schedules: () => request('mgmt', '/scheduler/schedules/'),
  history: (params) => request('mgmt', '/scheduler/history/', { params }),
  clearHistory: () => request('mgmt', '/scheduler/history/', { method: 'DELETE' }),
}
export const notifications = resource('comm', '/notifications/')

// admin: the review queue, admin actions, AI providers and the assistant.
export const adminApi = {
  // the signed-in admin's own MCP tokens and connections
  me: {
    mcp: () => request('admin', '/me/mcp'),
    // -> {id, name, prefix, ..., token} — the token is only ever returned here
    createToken: (name) => request('admin', '/me/mcp/tokens', { method: 'POST', body: { name } }),
    revokeToken: (id) => request('admin', `/me/mcp/tokens/${id}`, { method: 'DELETE' }),
    disconnect: (id) => request('admin', `/me/mcp/connections/${id}`, { method: 'DELETE' }),
  },
  overview: () => request('admin', '/overview'),
  meta: () => request('admin', '/meta'),
  tools: () => request('admin', '/tools'),
  review: {
    list: (params, opts) => request('admin', '/review', { params, ...opts }),
    counts: () => request('admin', '/review/counts'),
    scans: () => request('admin', '/review/scans'),
    scan: () => request('admin', '/review/scan', { method: 'POST' }),
  },
  // {action: suspend|reactivate|approve_stats|ignore|reopen, entity, ids, reason?, until?, note?}
  //   -> {results: [{id, ok, skipped?, error?}], ok_count, skipped_count, failed_count}
  act: (body) => request('admin', '/actions', { method: 'POST', body }),
  proposals: {
    list: (params, opts) => request('admin', '/proposals', { params, ...opts }),
    approve: (id) => request('admin', `/proposals/${id}/approve`, { method: 'POST' }),
    reject: (id, note) => request('admin', `/proposals/${id}/reject`, { method: 'POST', body: { note } }),
  },
  audit: {
    list: (params, opts) => request('admin', '/audit', { params, ...opts }),
    revert: (id) => request('admin', `/audit/${id}/revert`, { method: 'POST' }),
  },
  settings: {
    get: () => request('admin', '/settings'),
    save: (body) => request('admin', '/settings', { method: 'PUT', body }),
  },
  providers: {
    list: () => request('admin', '/providers'),
    create: (body) => request('admin', '/providers', { method: 'POST', body }),
    patch: (id, body) => request('admin', `/providers/${id}`, { method: 'PATCH', body }),
    remove: (id) => request('admin', `/providers/${id}`, { method: 'DELETE' }),
    kinds: () => request('admin', '/providers/kinds'),
    test: (id) => request('admin', `/providers/${id}/test`, { method: 'POST' }),
    models: (id) => request('admin', `/providers/${id}/models`),
  },
  chat: {
    list: () => request('admin', '/chat/conversations'),
    get: (id) => request('admin', `/chat/conversations/${id}`),
    create: (body = {}) => request('admin', '/chat/conversations', { method: 'POST', body }),
    rename: (id, title) => request('admin', `/chat/conversations/${id}`, { method: 'PATCH', body: { title } }),
    remove: (id) => request('admin', `/chat/conversations/${id}`, { method: 'DELETE' }),
    // events: start, thinking, text, tool_call, tool_result, proposal, usage, done | error
    send: (id, body, onEvent, opts) => stream('admin', `/chat/conversations/${id}/messages`, body, onEvent, opts),
  },
}

export const players = {
  ...resource('player', '/players/'),
  clone: (id) => request('player', `/players/${id}/clone/`, { method: 'POST' }),
}
export const formats = resource('player', '/formats/')
export const playerSettings = resource('player', '/settings/')
export const adcrewSettings = resource('player', '/adcrew-settings/')
export const monetization = resource('player', '/monetization/')
export const schedules = resource('player', '/schedules/')
export const headerBidding = resource('player', '/headerbidding/')
export const bidders = resource('player', '/bidders/')
export const adTags = resource('player', '/adtags/')
export const adScripts = resource('player', '/adscripts/')
export const styles = resource('player', '/styles/')
export const contents = resource('player', '/contents/')
export const blocklist = resource('player', '/blocklist/')
export const videos = resource('player', '/videos/')
export const playlists = resource('player', '/playlists/')
export const contentBrowser = {
  logos: () => request('player', '/logo-content/'),
  setLogo: (key, player_id) => request('player', '/logo-content/', { method: 'POST', body: { key, player_id } }),
  videos: () => request('player', '/video-content/'),
  setVideo: (url, player_id) => request('player', '/video-content/', { method: 'POST', body: { url, player_id } }),
}
